import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '@/services/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Rocket } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState('');

  // Handle ZewstID callback — extract token from URL hash
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      if (accessToken) {
        handleTokenLogin(accessToken);
      }
    }
  }, [location.hash]);

  // Redirect if already authenticated
  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  async function handleTokenLogin(accessToken: string) {
    setLoading(true);
    setError(null);
    try {
      await authService.setToken(accessToken);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }

  function handleZewstIdLogin() {
    window.location.href = authService.getLoginUrl();
  }

  // Dev mode: direct token entry
  async function handleDevLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!token.trim()) return;
    await handleTokenLogin(token.trim());
  }

  const isDev = import.meta.env.DEV;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <Rocket className="h-12 w-12 mx-auto text-primary mb-4" />
          <h1 className="text-3xl font-bold">Zewst</h1>
          <p className="text-muted-foreground mt-2">Build SaaS applications with AI</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Welcome</CardTitle>
            <CardDescription>
              Sign in with your ZewstID account to continue
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Authenticating...</span>
              </div>
            )}

            {!loading && (
              <>
                <Button
                  onClick={handleZewstIdLogin}
                  className="w-full"
                  size="lg"
                >
                  Sign in with ZewstID
                </Button>

                {isDev && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Dev mode</span>
                      </div>
                    </div>

                    <form onSubmit={handleDevLogin} className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="token">Bearer Token</Label>
                        <Input
                          id="token"
                          type="text"
                          placeholder="Paste JWT token..."
                          value={token}
                          onChange={(e) => setToken(e.target.value)}
                          className="font-mono text-xs"
                        />
                      </div>
                      <Button type="submit" variant="outline" className="w-full">
                        Login with Token
                      </Button>
                    </form>
                  </>
                )}
              </>
            )}
          </CardContent>
          <CardFooter>
            <p className="text-sm text-center w-full text-muted-foreground">
              Don't have an account?{' '}
              <a href={authService.getLoginUrl()} className="text-primary hover:underline">
                Sign up with ZewstID
              </a>
            </p>
          </CardFooter>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="hover:underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
